import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";

const OrderConfirmation = () => {
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!order) {
      api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setFailed(true));
    }
  }, [id, order]);

  if (failed) {
    return (
      <div className="mx-auto max-w-2xl px-5 pb-24 pt-40 text-center">
        <p className="font-serif text-3xl text-espresso">We couldn't find that order</p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-espresso px-6 py-3 text-sm font-semibold text-cream hover:bg-terra">Back to shop</Link>
      </div>
    );
  }
  if (!order) {
    return <div className="mx-auto max-w-2xl px-5 pb-24 pt-40 text-center font-mono text-sm text-clay/60">Loading your order…</div>;
  }

  const paid = order.payment_status === "paid";
  const cod = order.payment_method === "cod" && !paid;

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-8" data-testid="order-success-confirmation">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-light">
          <CheckCircle2 className="h-8 w-8 text-sage" />
        </span>
        <p className="mt-6 font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">Order placed</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-espresso sm:text-4xl">Thank you, {order.customer.name.split(" ")[0]}.</h1>
        <p className="mt-3 text-sm text-clay/70">
          Order <span className="font-mono font-semibold text-espresso" data-testid="order-id-text">#{order.id}</span> is confirmed
          and a confirmation email is on its way to {order.customer.email}.
        </p>
        <div className="mt-4">
          {paid ? (
            <span data-testid="payment-status-paid" className="inline-block rounded-full bg-sage-light px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-sage">
              Payment received via Razorpay
            </span>
          ) : cod ? (
            <span data-testid="payment-status-cod" className="inline-block rounded-full bg-ochre/15 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-ochre">
              Cash on delivery — pay {inr(order.total)} when it arrives
            </span>
          ) : (
            <span data-testid="payment-status-unpaid" className="inline-block rounded-full bg-terra-light px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-terra-dark">
              Payment pending — we'll share a secure payment link
            </span>
          )}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="mt-12 overflow-hidden rounded-2xl border border-linen bg-white">
        <div className="border-b border-linen bg-sand/60 px-6 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay/70">Order summary · #{order.id}</p>
        </div>
        <ul className="divide-y divide-linen px-6">
          {order.items.map((i) => (
            <li key={i.product_id} className="flex items-center gap-4 py-4">
              <img src={i.image} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
              <div className="flex-1">
                <p className="text-sm font-medium text-espresso">{i.name}</p>
                <p className="font-mono text-xs text-clay/60">× {i.qty}</p>
              </div>
              <span className="font-mono text-sm">{inr(i.line_total)}</span>
            </li>
          ))}
        </ul>
        <div className="space-y-1.5 border-t border-linen px-6 py-5 text-sm">
          <div className="flex justify-between text-clay/70"><span>Subtotal</span><span className="font-mono">{inr(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-sage"><span>Coupon {order.coupon_code}</span><span className="font-mono">−{inr(order.discount)}</span></div>}
          <div className="flex justify-between text-clay/70"><span>Shipping ({order.shipping_method})</span><span className="font-mono">{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
          <div className="flex justify-between pt-2 text-base font-semibold text-espresso"><span>Total</span><span className="font-mono text-lg">{inr(order.total)}</span></div>
        </div>
        <div className="border-t border-linen bg-sand/40 px-6 py-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay/70">Delivering to</p>
          <p className="mt-2 text-sm leading-relaxed text-espresso">
            {order.customer.name} · {order.customer.phone}<br />
            {order.customer.address}{order.customer.landmark ? `, near ${order.customer.landmark}` : ""}, {order.customer.city}, {order.customer.state} — {order.customer.pincode}
          </p>
        </div>
      </motion.div>

      <div className="mt-10 text-center">
        <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-espresso px-7 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-terra">
          Continue browsing
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmation;
